// IEA Site Survey: the rules behind the step 2 shell. Pure: data in, answers out.
// Words: IEA_OFFLINE_SHELL_WORDS.md rev 3 (approved 2026-10-14), IEA_REBASELINE_WORDS.md rev 2,
// IEA_1584_FIELD_REVIEW.md rev 2 (approved). Mockup: MOCKUP_IEA_SHELL.html (approved 2026-10-14).
// Unit tested: tests/unit/survey.test.ts.
//
// Names. The screens say IEA Site Survey. The stored authority keeps its name, ieaWalk.

import { readQrLink } from "./qr-link";

// ---- What the site pack holds ----

/** One registry asset, as the registry copy and the asset push write it. */
export interface PackAsset {
  assetId: string;
  location: string;
  equipmentType: string;
  /** Class code from the registry (MCC, PNL, UTL). */
  equipmentCode: string;
  nominalVoltage: string;
  nominalVolts: number | null;
  isOutOfService: boolean;
}

/** One saved link (Connections, IEA_WALK_SPEC.md 4.1). The shell reads the first four; the Map reads the rest. */
export interface PackLink {
  fromAsset: string;
  toAsset: string;
  goesTo: string;
  status: string;
  id?: string;
  fromPoint?: string;
  toPoint?: string;
  howKnown?: string;
  endLoadType?: string;
  endLoadHp?: string;
  endLoadTag?: string;
  notes?: string;
  recordedAt?: string;
  /** The Collect wire fields on this row (D23). The whole stored row, for Collect to read. */
  row?: Record<string, unknown>;
}

export interface SitePack {
  siteNumber: string;
  siteName: string;
  /** ISO time the pack was downloaded. */
  savedAt: string;
  assets: PackAsset[];
  /** Asset IDs with an IEA record saved. */
  ieaDone: string[];
  links: PackLink[];
  /** Class code to IEEE class family (mcc, swgr, jbox), from Equipment Classes. */
  ieeeClassOf: Record<string, string>;
  /** IEA Data records by Asset ID, as stored (Collect, step 4). */
  ieaRecords?: Record<string, Record<string, unknown>>;
  /** Model Tags as stored (modelTags/{tag}, AF_MAPPING_WORDS.md section 5). */
  modelTags?: Record<string, unknown>[];
  /** Where hand-moved labels sit on the Drawing (drawing/{Asset ID}, SURVEY_DRAWING_GRID_C1_WORDS.md section H). */
  drawing?: Record<string, unknown>[];
  /**
   * Every asset record as stored, removed ones included, by Asset ID. The Asset ID page reads its
   * answers and stamps from here (ASSET_ID_PAGE_WORDS.md). An older device copy without it reads
   * nothing until the pack is refreshed.
   */
  assetDocs?: Record<string, Record<string, unknown>>;
  /** Equipment Classes from the Lists page: the Asset ID page's Equipment Type dropdown. */
  classes?: { code: string; name: string; order: number | null; isActive: boolean }[];
  /** Field List values from the Lists page (Collect dropdowns). */
  fieldLists?: { listName: string; value: string; group?: string; order: number | null; isActive: boolean }[];
}

// ---- Words for an asset ----

/** "480 V". Blank when the registry has no voltage. */
export function voltsWords(a: Pick<PackAsset, "nominalVolts" | "nominalVoltage">): string {
  if (a.nominalVolts) return `${a.nominalVolts} V`;
  const m = /(\d{2,5})\s*V/i.exec(a.nominalVoltage);
  return m ? `${m[1]} V` : "";
}

/**
 * Three-phase unless the registry voltage says otherwise. Prefilled, confirmed in the field
 * (1584 4.11, 1584.2 Annex A). 1584 covers three-phase only, so single-phase gets flagged.
 */
export function phaseOf(nominalVoltage: string): "three-phase" | "single-phase" {
  return /1\s*(Ø|PH\b|phase)|single|120\s*\/\s*240/i.test(nominalVoltage) ? "single-phase" : "three-phase";
}

/** The housing an asset sits in: the longest other registry ID it starts with, at a dash. */
export function housingOf(assetId: string, ids: ReadonlySet<string>): string | null {
  const parts = assetId.split("-");
  for (let n = parts.length - 1; n >= 2; n--) {
    const parent = parts.slice(0, n).join("-");
    if (ids.has(parent)) return parent;
  }
  return null;
}

/** The ways out of a housing: assets whose housing is this one. In registry order. */
export function waysOutOf(housingId: string, assets: readonly PackAsset[]): PackAsset[] {
  const ids = new Set(assets.map((a) => a.assetId));
  return assets.filter((a) => a.assetId !== housingId && housingOf(a.assetId, ids) === housingId);
}

/**
 * The position in words, from the last part of the ID. MCC: "Column C, Position 4"
 * (C4, E2a). Anything else: "Position A1".
 */
export function positionWords(assetId: string, housingId: string, code: string): string {
  const seg = assetId.slice(housingId.length + 1);
  const m = /^([A-Za-z])(\d+[a-z]?)$/.exec(seg);
  if (code === "MCC" && m) return `Column ${m[1]!.toUpperCase()}, Position ${m[2]}`;
  return `Position ${seg}`;
}

/** True for the utility service (UTL), the top of every map. */
export function isUtility(a: Pick<PackAsset, "equipmentCode" | "equipmentType">): boolean {
  return a.equipmentCode === "UTL" || /^utility service$/i.test(a.equipmentType.trim());
}

// ---- Starting values (IEEE 1584 typical) ----

export interface Typical {
  gapMm: number;
  workingDistanceIn: number;
  electrode: "VCB" | "VCBB" | "HCB";
  gapBasis: string;
  distanceBasis: string;
  electrodeBasis: string;
}

/**
 * Typical bus gap and working distance by IEEE class family at 1000 V and below
 * (1584 Tables 8 and 10), and the electrode configuration to start from (1584 Annex C).
 * Prefilled, never final: the field confirms in step 3 and the app marks Typical or Measured.
 * Above 1000 V returns null: no Alltech gear today, and the field picks from the nameplate.
 */
export function typicalFor(code: string, ieeeClass: string, volts: number | null, isWayOut: boolean): Typical | null {
  if (volts !== null && volts > 1000) return null;
  const family = ieeeClass || "mcc";
  if (family === "swgr") {
    return {
      gapMm: 32,
      workingDistanceIn: 24,
      electrode: isWayOut ? "VCBB" : "VCB",
      gapBasis: "Typical, low-voltage switchgear",
      distanceBasis: "Typical, low-voltage switchgear",
      electrodeBasis: isWayOut ? "Typical for a breaker on the bus" : "Typical for the main bus",
    };
  }
  if (family === "jbox") {
    return {
      gapMm: 13,
      workingDistanceIn: 18,
      electrode: "VCB",
      gapBasis: "Typical, cable junction box",
      distanceBasis: "Typical, 1000 V and below",
      electrodeBasis: "Typical for a junction box",
    };
  }
  const bucket = code === "MCC" && isWayOut;
  return {
    gapMm: 25,
    workingDistanceIn: 18,
    electrode: code === "MCC" && !isWayOut ? "VCB" : "VCBB",
    gapBasis: "Typical, low-voltage MCCs and panelboards",
    distanceBasis: "Typical, 1000 V and below",
    electrodeBasis: bucket ? "Typical for an MCC bucket" : code === "MCC" ? "Typical for the MCC bus" : "Typical for a panelboard or enclosed switch",
  };
}

// ---- Traced to Service (IEA_OFFLINE_SHELL_WORDS.md section 17) ----

/**
 * Every asset a chain of saved ways out connects up to a utility service. Figured, never stored.
 * The service itself counts as traced. A housing is reached through any way out that feeds it,
 * and its ways out are traced with it. Removed links do not count.
 */
export function tracedSet(pack: Pick<SitePack, "assets" | "links">): Set<string> {
  const ids = new Set(pack.assets.map((a) => a.assetId));
  const children = new Map<string, string[]>();
  for (const a of pack.assets) {
    const h = housingOf(a.assetId, ids);
    if (h) children.set(h, [...(children.get(h) ?? []), a.assetId]);
  }
  const feeds = new Map<string, string[]>();
  for (const l of pack.links) {
    if (l.status === "Removed" || !l.toAsset || l.goesTo !== "Equipment") continue;
    // A bucket fed by its own conductors (To Point is its position) traces that bucket, not the housing.
    const tp = l.toPoint ?? "";
    const to = tp && !["Main", "Bus", "Primary", "Normal", "Emergency"].includes(tp) && ids.has(`${l.toAsset}-${tp}`) ? `${l.toAsset}-${tp}` : l.toAsset;
    feeds.set(l.fromAsset, [...(feeds.get(l.fromAsset) ?? []), to]);
  }
  const traced = new Set<string>();
  const queue = pack.assets.filter(isUtility).map((a) => a.assetId);
  while (queue.length) {
    const id = queue.shift()!;
    if (traced.has(id)) continue;
    traced.add(id);
    for (const c of children.get(id) ?? []) queue.push(c);
    for (const t of feeds.get(id) ?? []) queue.push(t);
  }
  return traced;
}

// ---- Scan (IEA_OFFLINE_SHELL_WORDS.md section 16) ----

export type ScanResult =
  | { kind: "open"; assetId: string }
  | { kind: "notInRegistry"; assetId: string }
  | { kind: "otherSite"; assetId: string; siteNumber: string }
  | { kind: "qew" }
  | { kind: "notSafeTag" };

const SAFETAG_HOSTS = new Set(["getsafetag.com", "www.getsafetag.com", "test.getsafetag.com", "getsafetag-prod.web.app", "getsafetag-dev.web.app"]);

/** What a scanned QR, or a typed ID, opens in the survey. Works with no signal. */
export function resolveScan(input: string, hereHost: string, siteNumber: string, assetIds: ReadonlySet<string>): ScanResult {
  const text = input.trim();
  let assetId = "";
  if (/^https?:\/\//i.test(text)) {
    let url: URL;
    try {
      url = new URL(text);
    } catch {
      return { kind: "notSafeTag" };
    }
    if (!SAFETAG_HOSTS.has(url.hostname.toLowerCase()) && url.host !== hereHost) return { kind: "notSafeTag" };
    if (url.searchParams.get("quiz") !== null && url.searchParams.get("aid") === null) return { kind: "qew" };
    const read = readQrLink(text);
    if (read.kind === "badge") return { kind: "qew" };
    if (read.kind !== "asset") return { kind: "notSafeTag" };
    assetId = read.assetId;
  } else {
    if (/^(\d{2}-(QEW|CON)\d{2}|ALL-ADMIN-\d{3})$/i.test(text)) return { kind: "qew" };
    if (!/^\d{2}(-[A-Za-z0-9]+)+$/.test(text)) return { kind: "notSafeTag" };
    assetId = text;
  }
  const site = assetId.slice(0, 2);
  if (site !== siteNumber) return { kind: "otherSite", assetId, siteNumber: site };
  if (assetIds.has(assetId)) return { kind: "open", assetId };
  // The typed fallback is forgiving about case only when it is not ambiguous.
  const match = [...assetIds].filter((x) => x.toLowerCase() === assetId.toLowerCase());
  if (match.length === 1) return { kind: "open", assetId: match[0]! };
  return { kind: "notInRegistry", assetId };
}

// ---- Pre-survey check (IEA_OFFLINE_SHELL_WORDS.md section 7) ----

export type Tone = "green" | "amber" | "red";

export interface CheckLine {
  label: string;
  text: string;
  tone: Tone;
  pill: string;
}

export interface CheckInput {
  online: boolean;
  /** The site pack on this device, or null. */
  packSavedAt: string | null;
  packCounts: { assets: number; iea: number; links: number } | null;
  todayIso: string;
  homeScreenApp: boolean;
  waiting: number;
  /** Saves the cloud refused. They never send on their own, so they always warn. */
  refused?: number;
  /** Another device's open survey of this site with unsent work, or null. */
  otherDevice: { deviceName: string; waiting: number; lastSeen: string } | null;
  /** First utility service in the pack, or null. Unknown with no pack. */
  utility: string | null;
}

export interface CheckResult {
  lines: CheckLine[];
  /** Plain lines saying what to do. Any one blocks Start Survey. */
  blocks: string[];
  warnings: boolean;
}

export function preCheck(i: CheckInput): CheckResult {
  const blocks: string[] = [];
  const lines: CheckLine[] = [];
  const hasPack = i.packSavedAt !== null;

  lines.push({
    label: "Signal",
    text: i.online ? "Online" : "Offline",
    tone: i.online ? "green" : hasPack ? "amber" : "red",
    pill: i.online ? "Online" : "Offline",
  });

  if (!hasPack) {
    lines.push({ label: "Site pack", text: "None on this device", tone: "red", pill: "None" });
    blocks.push(i.online ? "Download the site pack before you survey." : "No signal and no site pack. Find signal and download the site pack.");
  } else {
    const c = i.packCounts!;
    const counts = `${c.assets} assets · ${c.iea} IEA records · ${c.links} links · ${packTime(i.packSavedAt!)}`;
    const today = i.packSavedAt!.slice(0, 10) === i.todayIso.slice(0, 10);
    if (!i.homeScreenApp) lines.push({ label: "Site pack", text: `${counts}. Opened in a browser tab: the browser may clear it. Survey from the Home Screen app.`, tone: "amber", pill: "Browser tab" });
    else lines.push({ label: "Site pack", text: counts, tone: today ? "green" : "amber", pill: today ? "Today" : "Older" });
  }

  lines.push({
    label: "Outbox",
    text: i.waiting ? `${i.waiting} ${i.waiting === 1 ? "item" : "items"} waiting to send` : "Nothing waiting",
    tone: i.waiting ? "amber" : "green",
    pill: i.waiting ? "Waiting" : "Empty",
  });

  lines.push(
    i.otherDevice
      ? {
          label: "Survey in progress",
          text: `${i.otherDevice.deviceName} has ${i.otherDevice.waiting} waiting, last seen ${i.otherDevice.lastSeen}`,
          tone: "amber",
          pill: "Other device",
        }
      : { label: "Survey in progress", text: "No other device", tone: "green", pill: "Clear" },
  );

  if (hasPack) {
    if (i.utility) lines.push({ label: "Utility service", text: `${i.utility} in the registry`, tone: "green", pill: "Found" });
    else {
      lines.push({ label: "Utility service", text: "No utility service in the registry", tone: "red", pill: "Missing" });
      blocks.push("Add the utility service (UTL) through the asset push first. The survey starts at the service.");
    }
  }

  return { lines, blocks, warnings: lines.some((l) => l.tone === "amber") };
}

// ---- The Map's warning line (IEA_SURVEY_ENTRY_WORDS.md section 4) ----

export type MapWarningKind = "other" | "tab" | "older" | "outbox";

export interface MapWarning {
  kind: MapWarningKind;
  text: string;
}

export interface MapWarnLine {
  /** The most important warning. The only one written on the line. */
  first: MapWarning;
  /** Warnings left over. Shown as "and N more", which opens the site page. */
  more: number;
  /** Refresh Site Pack sits on the line when the pack is older or opened in a browser tab. */
  refresh: boolean;
}

/** Every warning that applies, most important first: Other device, Browser tab, Older pack, Outbox. */
export function mapWarnings(i: CheckInput): MapWarning[] {
  const w: MapWarning[] = [];
  if (i.otherDevice) w.push({ kind: "other", text: `Other device: ${i.otherDevice.deviceName} has ${i.otherDevice.waiting} waiting, last seen ${i.otherDevice.lastSeen}` });
  if (i.packSavedAt !== null && !i.homeScreenApp) w.push({ kind: "tab", text: "Browser tab: the browser may clear the site pack. Survey from the Home Screen app." });
  if (i.packSavedAt !== null && i.packSavedAt.slice(0, 10) !== i.todayIso.slice(0, 10)) w.push({ kind: "older", text: `Site pack from ${packTime(i.packSavedAt)}` });
  // Online, a save sits in the Outbox a second or two and sends. Warning on that flashed the line under every tap
  // (Robert 2026-10-30 6:52 AM). The Outbox warns only when it means something: no signal, or a refused save.
  if (i.waiting && (!i.online || (i.refused ?? 0) > 0)) w.push({ kind: "outbox", text: `${i.waiting} waiting in the Outbox` });
  return w;
}

/** One line only: the first warning and how many more. Null when there is nothing to warn about. */
export function mapWarnLine(i: CheckInput): MapWarnLine | null {
  const w = mapWarnings(i);
  const first = w[0];
  if (!first) return null;
  return { first, more: w.length - 1, refresh: w.some((x) => x.kind === "tab" || x.kind === "older") };
}

/** "Oct 14, 6:10 AM" in the device's own time. */
export function packTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// ---- Site counts on the survey page ----

export function surveyCounts(pack: SitePack): { total: number; ieaDone: number; notTraced: number; studyReady: number } {
  const traced = tracedSet(pack);
  const done = new Set(pack.ieaDone);
  const live = pack.assets.filter((a) => !a.isOutOfService);
  return {
    total: live.length,
    ieaDone: live.filter((a) => done.has(a.assetId)).length,
    notTraced: live.filter((a) => !traced.has(a.assetId)).length,
    // Study Ready is figured from the step 3 cards. Nothing can be ready before them.
    studyReady: 0,
  };
}

// ---- This device ----

/** Aa Large by default on an iPad-sized touch screen, Standard on a phone or desktop. */
export function defaultLarge(maxTouchPoints: number, shortSide: number): boolean {
  return maxTouchPoints > 1 && shortSide >= 700;
}

/** A device name the survey will store: trimmed, at most 40 characters, never blank. */
export function cleanDeviceName(raw: string): string {
  const v = raw.replace(/\s+/g, " ").trim().slice(0, 40);
  return v || "This device";
}
